import { ReportType } from '@constants'
import { network } from '@utils'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { ReportColumn } from '@utils/generateReport'

export type ReportComparisonMode = 'amount' | 'percent'
export type ReportColumnUnit = 'amount' | 'percent'

export type ReportRequestBody = {
  columns: ReportColumn[]
  extend_accounts?: boolean
  append_comparison_columns?: boolean
  comparison_mode?: ReportComparisonMode
  tag_ids?: number[]
  account_number?: string
  document_number?: string
  vat_entry_group?: string
  entries_page?: number
  entries_page_size?: number
  include_entry_lines?: boolean
}

export type ReportEntriesPagination = {
  page: number
  page_size: number
  total_count: number
  page_count: number
}

export type AccountingReportAccount = {
  number: string
  name: string
  values: (number | null)[]
  labels?: string[]
}

export type AccountingReportRow = {
  name: string
  level: number
  is_sum_row: boolean
  values: (number | null)[]
  labels?: string[]
  accounts: AccountingReportAccount[]
}

export type AccountingReportJson = {
  report_type: string
  date_from?: string | null
  date_to?: string | null
  labels: string[]
  rows: AccountingReportRow[]
  comparison_mode?: ReportComparisonMode
  column_units?: ReportColumnUnit[]
}

export type ReportEntryAttachment = {
  id: number
  name: string
  type: string
}

/**
 * Attachments keyed by document ID. Entries reference this map through their
 * `document_id` so a document shared by several entries is sent once.
 */
export type ReportAttachmentsByDocument = Record<string, ReportEntryAttachment[]>

export const reportAttachmentsFor = (
  attachmentsByDocument: ReportAttachmentsByDocument | undefined,
  documentId: number | null | undefined
): ReportEntryAttachment[] | undefined =>
  documentId == null ? undefined : attachmentsByDocument?.[String(documentId)]

export type LedgerReportEntry = {
  number: string
  date: string
  debet: number
  credit: number
  balance_csum: number
  description?: string
  document_id?: number | null
}

export type LedgerReportAccount = {
  number: string
  name: string
  opening_balance: number
  closing_balance?: number
  entries: LedgerReportEntry[]
  entries_count?: number
  entries_pagination?: ReportEntriesPagination
  totals?: Record<string, number> | null
}

export type LedgerJson = {
  report_type: string
  date_from?: string | null
  date_to?: string | null
  accounts: LedgerReportAccount[]
  attachments_by_document?: ReportAttachmentsByDocument
  totals?: Record<string, number> | null
}

export type JournalReportEntry = {
  account_number: string
  account_name: string
  debet: number
  credit: number
  description?: string
}

export type JournalReportDocument = {
  number: string
  date?: string | null
  document_id?: number | null
  entries: JournalReportEntry[]
  entries_count?: number
  entries_pagination?: ReportEntriesPagination
  totals?: Record<string, number> | null
}

export type JournalJson = {
  report_type: string
  date_from?: string | null
  date_to?: string | null
  documents: JournalReportDocument[]
  totals?: Record<string, number> | null
}

export type VatReportEntry = {
  document_number: string
  document_date: string
  document_description?: string
  account: string
  debet: number
  credit: number
  vat_code?: string
  vat_rate?: string
  document_id?: number | null
}

export type VatSummaryLine = {
  amount: number
  entries: VatReportEntry[]
  entries_count?: number
  entries_pagination?: ReportEntriesPagination
  label?: string
  code?: string
}

export type DeVatSection = {
  id: string
  label: string
  codes: string[]
  always_visible?: boolean
  totals?: string[]
}

export type VatJson = {
  report_type: string
  date_from?: string | null
  date_to?: string | null
  summary?: Record<string, unknown>
  totals?: Record<string, number>
  totals_labels?: Record<string, string>
  mapping?: Record<string, number | VatSummaryLine>
  de_sections?: DeVatSection[]
  recapitulative_statement_required: boolean
}

export type EuVatContactRow = {
  country_code: string
  vat_id: string
  name: string
  vat_handling: string
  net_amount: number
  vat_id_missing: boolean
  contact_missing: boolean
  document_id?: number | null
  document_number?: string | null
}

export type EuVatContactJson = {
  report_type: string
  date_from?: string | null
  date_to?: string | null
  business_vat_id: string
  rows: EuVatContactRow[]
  private_person_rows?: EuVatContactRow[]
}

export type ReconciliationEntry = {
  number: string
  date: string
  description?: string
  amount: number
  document_id?: number | null
}

export type ReconciliationAccount = {
  number: string
  name: string
  opening_balance: number
  closing_balance: number
  entries: ReconciliationEntry[]
  entries_count?: number
  entries_pagination?: ReportEntriesPagination
}

export type ReconciliationRow = {
  name: string
  level: number
  is_empty: boolean
  accounts: ReconciliationAccount[]
}

export type BalanceSheetReconciliationJson = {
  report_type: string
  date_from?: string | null
  date_to?: string | null
  rows: ReconciliationRow[]
  attachments_by_document?: ReportAttachmentsByDocument
}

export type InteractiveReportData =
  | AccountingReportJson
  | LedgerJson
  | JournalJson
  | BalanceSheetReconciliationJson
  | VatJson
  | EuVatContactJson

export type InteractiveReportKey =
  | 'balance_sheet'
  | 'income_statement'
  | 'ledger'
  | 'journal'
  | 'balance_sheet_specifications'
  | 'vat'
  | 'eu_vat'

const REPORT_PATH: Record<InteractiveReportKey, string> = {
  balance_sheet: 'balance-sheet',
  income_statement: 'income-statement',
  ledger: 'ledger',
  journal: 'journal',
  balance_sheet_specifications: 'balance-sheet-reconciliation',
  vat: 'vat-report',
  eu_vat: 'eu-vat-contact-report'
}

export const INTERACTIVE_REPORT_TYPE: Record<InteractiveReportKey, ReportType> = {
  balance_sheet: ReportType.BALANCE_SHEET,
  income_statement: ReportType.INCOME_STATEMENT,
  ledger: ReportType.LEDGER,
  journal: ReportType.JOURNAL,
  balance_sheet_specifications: ReportType.BALANCE_SHEET_RECONCILIATION,
  vat: ReportType.VAT_REPORT,
  eu_vat: ReportType.EU_VAT_CONTACT_REPORT
}

/** `report_type` values returned by the interactive JSON endpoints (not always `ReportType`). */
export const INTERACTIVE_REPORT_JSON_TYPE: Record<InteractiveReportKey, string> = {
  balance_sheet: ReportType.BALANCE_SHEET,
  income_statement: ReportType.INCOME_STATEMENT,
  ledger: 'ledger',
  journal: 'journal',
  balance_sheet_specifications: 'balance_sheet_reconciliation',
  vat: 'vat_report',
  eu_vat: 'eu_vat_contact_report'
}

const normalizeReportType = (value: string) => value.trim().toLowerCase().replace(/-/g, '_')

type FetchReportProps = {
  slug: string
  reportKey: InteractiveReportKey
  body: ReportRequestBody
}

const postReport = async <T>({ slug, reportKey, body }: FetchReportProps): Promise<T> => {
  const url = `/v1/business/${slug}/report/${REPORT_PATH[reportKey]}/`
  const { data } = await network.httpClient.post<T>(url, body, { timeout: 120000 })
  return data
}

export const fetchAccountingReport = (props: FetchReportProps) =>
  postReport<AccountingReportJson>(props)

export const fetchLedgerReport = (props: FetchReportProps) => postReport<LedgerJson>(props)

export const fetchJournalReport = (props: FetchReportProps) => postReport<JournalJson>(props)

export const fetchVatReport = (props: FetchReportProps) => postReport<VatJson>(props)

export const fetchEuVatContactReport = (props: FetchReportProps) =>
  postReport<EuVatContactJson>(props)

export type UseInteractiveReportArgs = {
  slug: string
  reportKey: InteractiveReportKey
  body: ReportRequestBody
  enabled?: boolean
}

export const interactiveReportQueryKey = (
  slug: string,
  reportKey: InteractiveReportKey,
  body: ReportRequestBody
) => [slug, 'interactive-report', reportKey, body] as const

export const isReportDataForKey = (
  data: InteractiveReportData | undefined,
  reportKey: InteractiveReportKey
): boolean =>
  Boolean(
    data?.report_type &&
      normalizeReportType(data.report_type) ===
        normalizeReportType(INTERACTIVE_REPORT_JSON_TYPE[reportKey])
  )

const keepPreviousReportData = <T extends InteractiveReportData>(
  slug: string,
  reportKey: InteractiveReportKey
) => {
  return (
    previousData: T | undefined,
    previousQuery: { queryKey: readonly unknown[] } | undefined
  ) => {
    if (!previousData || !previousQuery) return undefined
    const prevKey = previousQuery.queryKey as ReturnType<typeof interactiveReportQueryKey>
    if (prevKey[0] !== slug || prevKey[2] !== reportKey) return undefined
    return previousData
  }
}

export const useInteractiveReport = <T extends InteractiveReportData = InteractiveReportData>({
  slug,
  reportKey,
  body,
  enabled = true
}: UseInteractiveReportArgs) => {
  return useQuery<T, Error, T, ReturnType<typeof interactiveReportQueryKey>>({
    queryKey: interactiveReportQueryKey(slug, reportKey, body),
    queryFn: () => postReport<T>({ slug, reportKey, body }),
    enabled: Boolean(slug) && enabled,
    staleTime: 30_000,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    placeholderData: keepPreviousReportData<T>(slug, reportKey) as any
  })
}

export const useLedgerReport = ({
  slug,
  body,
  enabled = true
}: {
  slug: string
  body: ReportRequestBody
  enabled?: boolean
}) => {
  return useQuery({
    queryKey: interactiveReportQueryKey(slug, 'ledger', body),
    queryFn: () => fetchLedgerReport({ slug, reportKey: 'ledger', body }),
    enabled: Boolean(slug) && enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData
  })
}
